<template>
  <div class="mb-4 flex items-center justify-between">
    <div class="flex items-center gap-3">
      <div class="flex items-center gap-2">
        <!-- Change indicator -->
        <div
          v-if="props.hasChanges && isDocumentModified"
          class="h-2 w-2 rounded-full bg-amber-500"
          :title="documentChangeDescription"
        ></div>

        <div
          v-if="isEditing"
          class="flex w-full max-w-xl items-center gap-3"
        >
          <div class="relative min-w-[200px] flex-1 sm:min-w-[300px]">
            <input
              v-model="newName"
              type="text"
              class="h-10 w-full rounded-md border-0 px-3 pr-24 text-gray-900 ring-1 ring-inset ring-purple-300
                focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm"
              :title="newName"
            />
            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">.pdf</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="absolute right-12 top-1/2 h-8 w-8 -translate-y-1/2 text-purple-600 hover:bg-purple-50
                      hover:text-purple-700"
                    :disabled="isGeneratingTitle"
                    @click="generateAITitle"
                  >
                    <AiEditIcon
                      v-if="!isGeneratingTitle"
                      class="h-4 w-4"
                    />
                    <Loader
                      v-else
                      class="h-4 w-4 animate-spin"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent class="border-black bg-black text-white">
                  <p class="text-white">Rename with AI</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 text-green-600 hover:text-green-700"
              @click="saveNewName"
            >
              <CheckCircle2 class="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 text-red-600 hover:text-red-700"
              @click="cancelEditing"
            >
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div
          v-else
          class="flex w-full max-w-xl items-center gap-2"
        >
          <span
            :class="[
              'min-w-0 flex-1 truncate text-lg font-semibold transition-colors duration-200',
              isDragOver ? 'text-purple-600' : 'text-gray-900'
            ]"
            :title="document.name"
          >
            {{ document.name }}
          </span>
          <button
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white
              text-gray-600 shadow-sm transition-colors hover:border-purple-400 hover:text-purple-600"
            :title="t('editDocumentName')"
            @click="startEditing"
          >
            <Pencil class="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-3">
      <span
        :class="[
          'text-sm transition-colors duration-200',
          isDragOver ? 'text-purple-600' : 'text-gray-600'
        ]"
      >
        {{ document.pages.length }} {{ t('pages') }}
      </span>
      <Button
        variant="secondary"
        size="sm"
        :disabled="isGeneratingPreview"
        @click="previewDocument"
      >
        <Loader
          v-if="isGeneratingPreview"
          class="mr-2 h-4 w-4 animate-spin"
        />
        <Eye class="mr-2 h-4 w-4" />
        {{ t('preview') }}
      </Button>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              :disabled="document.pages.length <= 1 || !canSplitWithAI"
              class="flex items-center gap-2"
              @click="splitWithAI"
            >
              <Loader
                v-if="isSplittingWithAI"
                class="mr-2 h-4 w-4 animate-spin"
              />
              <Scissors
                v-else
                class="mr-2 h-4 w-4"
              />
              {{ t('splitWithAI') }}
            </Button>
          </TooltipTrigger>
          <TooltipContent
            v-if="!canSplitWithAI && document.pages.length > 1"
            class="border-black bg-black text-white"
          >
            <p class="text-white">{{ t('aiSplitRequiresOcr') }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            :disabled="totalDocuments <= 1"
            class=""
          >
            <MergeHorizontalIcon class="mr-2 h-4 w-4" />
            {{ t('merge') }}
            <ChevronDown class="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="max-h-80 overflow-y-auto">
          <!-- Merge with adjacent documents -->
          <DropdownMenuItem
            :disabled="documentIndex === 0"
            @click="mergeWithDocumentAbove"
          >
            <Merge class="mr-2 h-4 w-4" />
            {{ t('mergeWithDocumentAbove') }}
          </DropdownMenuItem>
          <DropdownMenuItem
            :disabled="documentIndex === totalDocuments - 1"
            @click="mergeWithDocumentBelow"
          >
            <MergeDownIcon class="mr-2 h-4 w-4" />
            {{ t('mergeWithDocumentBelow') }}
          </DropdownMenuItem>

          <!-- Separator -->
          <div
            v-if="availableDocumentGroups.length > 0"
            class="my-2 border-t border-gray-200"
          ></div>

          <DropdownMenuItem
            v-for="targetDoc in availableDocumentGroups"
            :key="targetDoc.name"
            class="py-3"
            @click="mergeWithSpecificGroup(targetDoc.name)"
          >
            <MergeHorizontalIcon class="mr-2 h-4 w-4" />
            {{ targetDoc.name }}
          </DropdownMenuItem>

          <!-- Separator -->
          <div class="my-2 border-t border-gray-200"></div>

          <!-- Merge all documents (only show if there are multiple documents) -->
          <DropdownMenuItem
            v-if="totalDocuments > 1"
            class="text-black"
            @click="mergeAllDocuments"
          >
            <MergeAllIcon class="mr-2 h-4 w-4" />
            {{ t('mergeAllDocuments') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
          >
            <MoreVertical class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem @click="duplicateDocument">
            <Copy class="mr-2 h-4 w-4" />
            {{ t('duplicate') }}
          </DropdownMenuItem>
          <DropdownMenuItem
            class="text-red-600"
            @click="deleteDocument"
          >
            <Trash2 class="mr-2 h-4 w-4" />
            {{ t('delete') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>

  <!-- PDF Preview Modal -->
  <PDFViewer
    v-model:isOpen="showPdfViewer"
    v-model:url="pdfViewerUrl"
    :filename="pdfViewerFilename"
    :document="previewDocumentMeta || undefined"
    :initial-page="1"
    :batch-size="5"
    :fit-page="true"
    :hide-topbar="true"
    :disable-edit-page="true"
    @document-updated="onPdfDocumentUpdated"
  />

  <!-- Delete Confirmation Dialog -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('deleteTitle') }}</DialogTitle>
        <DialogDescription>
          {{ t('deleteDescription', { name: document.name?.trim() || 'this document' }) }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="handleDelete"
        >
          {{ t('confirmDelete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Merge Size Warning Dialog -->
  <Dialog
    :open="showMergeSizeWarning"
    @update:open="closeMergeSizeWarning"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-amber-600">{{ t('mergeSizeWarning') }}</DialogTitle>
        <DialogDescription class="space-y-2">
          <p>
            {{
              t('mergeSizeWarningDescription', {
                estimatedSize: mergeSizeValidation?.estimatedSize || '',
                maxSize: mergeSizeValidation?.maxSize || ''
              })
            }}
          </p>
          <p class="font-medium text-amber-600">
            {{ t('mergeSizeExceedsBy', { size: mergeSizeValidation?.exceedsBy || '' }) }}
          </p>
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="closeMergeSizeWarning"
        >
          {{ t('cancelMerge') }}
        </Button>
        <Button
          variant="destructive"
          @click="proceedWithMerge"
        >
          {{ t('proceedAnyway') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import AiEditIcon from '@/components/icons/AiEditIcon.vue'
  import MergeAllIcon from '@/components/icons/MergeAllIcon.vue'
  import MergeDownIcon from '@/components/icons/MergeDownIcon.vue'
  import MergeHorizontalIcon from '@/components/icons/MergeHorizontalIcon.vue'
  import PDFViewer from '@/components/pdf/NewPdfViewer.vue'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { useToast } from '@/components/ui/toast'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { DocumentsService } from '@/types/generated'
  import {
    CheckCircle2,
    ChevronDown,
    Copy,
    Eye,
    Loader,
    Merge,
    MoreVertical,
    Pencil,
    Scissors,
    Trash2,
    X
  } from 'lucide-vue-next'
  import { computed, ref } from 'vue'
  import type { DocumentGroup } from './composables/useDocumentOrganization'
  import {
    useDocumentOrganization,
    useDocumentOrganizationContext
  } from './composables/useDocumentOrganization'

  interface Props {
    document: DocumentGroup
    documentIndex: number
    totalDocuments: number
    originalDocumentState: DocumentGroup[]
    hasChanges: boolean
    dragOverDocumentGroup: number | null
    caseId: string
    pageRotations: Map<string, number>
  }

  interface Emits {
    (e: 'documentChange'): void
    (e: 'mergeDocuments', sourceIndex: number, targetIndex: number): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  // Get organizedDocuments from context
  const {
    organizedDocuments,
    addPendingNameChange,
    removePendingNameChange,
    validateMergeSize,
    validateMergeAllSize,
    formatFileSize
  } = useDocumentOrganizationContext()

  const isGeneratingPreview = ref(false)
  const previewPdfData = ref<Uint8Array | null>(null)
  const previewDocumentMeta = ref<any | null>(null)

  const { t } = useTranslation({
    en: {
      editDocumentName: 'Edit document name',
      pages: 'pages',
      preview: 'Preview',
      splitWithAI: 'Split with AI',
      renameWithAi: 'Rename with AI',
      generatingPreview: 'Generating preview... ({progress}%)',
      previewSuccess: 'Preview generated successfully!',
      previewFailed: 'Failed to generate preview. Please try again.',
      targetDocNotFound: 'Target document not found',
      cannotMergeWithSelf: 'Cannot merge document with itself',
      mergedDocument: 'Merged Document',
      noDocumentUrl: 'No pages available to preview',
      aiAnalyzing: 'Analyzing document with AI...',
      documentDeleted: 'Document deleted successfully',
      documentMissingOrEmpty: 'Document not found or has no pages',
      copyOf: '{name} - Copy',
      nameChangedFromTo: 'Name changed from "{old}" to "{new}"',
      addedPages: 'Added {count} pages',
      removedPages: 'Removed {count} pages',
      pageOrderChanged: 'Page order changed',
      merge: 'Merge...',
      mergeWithDocumentAbove: 'Merge with document above',
      mergeWithDocumentBelow: 'Merge with document below',
      mergeAllDocuments: 'Merge all documents',
      duplicate: 'Duplicate',
      delete: 'Delete',
      aiSplitRequiresOcr: 'AI split requires OCR processing',
      documentNameUpdated: 'Document name updated successfully',
      documentNameQueuedForSave: 'Document name will be saved when you click Save Documents',
      failedToUpdateName: 'Failed to update document name',
      aiTitleGenerated: 'AI Title Generated',
      aiTitleGeneratedDescription: 'AI has generated a new title for this document',
      aiTitleError: 'AI Title Error',
      failedToGenerateTitle: 'Failed to generate AI title',
      aiSplitSuccess: 'AI Split Success',
      aiSplitDescription: 'Document split into {count} groups using AI',
      aiAnalysisComplete: 'AI Analysis Complete',
      aiAnalysisNoSplit: 'Document analyzed but no split was needed',
      aiSplitError: 'Failed to split document with AI',
      noDocumentAbove: 'No document above to merge with',
      noDocumentBelow: 'No document below to merge with',
      needMultipleDocuments: 'Need at least 2 documents to merge',
      documentsMerged: 'Documents merged successfully',
      deleteTitle: 'Delete Document',
      deleteDescription: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Delete',
      error: 'Error',
      success: 'Success',
      info: 'Info',
      documentDuplicated: 'Document Duplicated',
      mergeSizeWarning: 'Document Size Limit Warning',
      mergeSizeWarningDescription:
        'Merging these documents would create a file of approximately {estimatedSize}, which exceeds the maximum allowed size of {maxSize}. The save operation will fail if you proceed.',
      mergeSizeExceedsBy: 'Exceeds limit by: {size}',
      proceedAnyway: 'Merge anyway',
      cancelMerge: 'Cancel'
    },
    de: {
      editDocumentName: 'Dokumentname bearbeiten',
      pages: 'Seiten',
      preview: 'Vorschau',
      splitWithAI: 'Mit KI aufteilen',
      renameWithAi: 'Mit KI umbenennen',
      generatingPreview: 'Vorschau wird erstellt... ({progress}%)',
      previewSuccess: 'Vorschau erfolgreich erstellt!',
      previewFailed: 'Vorschau konnte nicht erstellt werden. Bitte erneut versuchen.',
      targetDocNotFound: 'Zieldokument nicht gefunden',
      cannotMergeWithSelf: 'Dokument kann nicht mit sich selbst zusammengeführt werden',
      mergedDocument: 'Zusammengeführtes Dokument',
      noDocumentUrl: 'Keine Seiten für die Vorschau verfügbar',
      aiAnalyzing: 'Dokument wird mit KI analysiert...',
      documentDeleted: 'Dokument erfolgreich gelöscht',
      documentMissingOrEmpty: 'Dokument nicht gefunden oder hat keine Seiten',
      copyOf: '{name} - Kopie',
      nameChangedFromTo: 'Name von „{old}" zu „{new}" geändert',
      addedPages: '{count} Seiten hinzugefügt',
      removedPages: '{count} Seiten entfernt',
      pageOrderChanged: 'Seitenreihenfolge geändert',
      merge: 'Zusammenführen...',
      mergeWithDocumentAbove: 'Mit Dokument darüber zusammenführen',
      mergeWithDocumentBelow: 'Mit Dokument darunter zusammenführen',
      mergeAllDocuments: 'Alle Dokumente zusammenführen',
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      aiSplitRequiresOcr: 'KI-Aufteilung erfordert OCR-Verarbeitung',
      documentNameUpdated: 'Dokumentname erfolgreich aktualisiert',
      documentNameQueuedForSave: 'Dokumentname wird beim Klick auf Dokumente speichern gespeichert',
      failedToUpdateName: 'Fehler beim Aktualisieren des Dokumentnamens',
      aiTitleGenerated: 'KI-Titel generiert',
      aiTitleGeneratedDescription: 'KI hat einen neuen Titel für dieses Dokument generiert',
      aiTitleError: 'KI-Titel Fehler',
      failedToGenerateTitle: 'Fehler beim Generieren des KI-Titels',
      aiSplitSuccess: 'KI-Aufteilung erfolgreich',
      aiSplitDescription: 'Dokument mit KI in {count} Gruppen aufgeteilt',
      aiAnalysisComplete: 'KI-Analyse abgeschlossen',
      aiAnalysisNoSplit: 'Dokument analysiert, aber keine Aufteilung erforderlich',
      aiSplitError: 'Fehler beim Aufteilen des Dokuments mit KI',
      noDocumentAbove: 'Kein Dokument darüber zum Zusammenführen',
      noDocumentBelow: 'Kein Dokument darunter zum Zusammenführen',
      needMultipleDocuments: 'Mindestens 2 Dokumente zum Zusammenführen erforderlich',
      documentsMerged: 'Dokumente erfolgreich zusammengeführt',
      deleteTitle: 'Dokument löschen',
      deleteDescription:
        'Sind Sie sicher, dass Sie "{name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirmDelete: 'Löschen',
      error: 'Fehler',
      success: 'Erfolg',
      info: 'Info',
      documentDuplicated: 'Dokument dupliziert',
      mergeSizeWarning: 'Warnung zur Dokumentgröße',
      mergeSizeWarningDescription:
        'Das Zusammenführen dieser Dokumente würde eine Datei von ungefähr {estimatedSize} erstellen, was die maximal erlaubte Größe von {maxSize} überschreitet. Der Speichervorgang wird fehlschlagen, wenn Sie fortfahren.',
      mergeSizeExceedsBy: 'Überschreitung: {size}',
      proceedAnyway: 'Trotzdem zusammenführen',
      cancelMerge: 'Abbrechen'
    }
  })

  const { createPdfFromPages } = useDocumentOrganization(t)
  // Local state for editing
  const isEditing = ref(false)
  const newName = ref('')
  const isSplittingWithAI = ref(false)

  // PDF preview state
  const showPdfViewer = ref(false)
  const pdfViewerUrl = ref('')
  const pdfViewerFilename = ref('')
  const isGeneratingTitle = ref(false)

  // Delete modal state
  const showDeleteModal = ref(false)

  // Merge size warning modal state
  const showMergeSizeWarning = ref(false)
  const pendingMergeAction = ref<(() => void) | null>(null)
  const mergeSizeValidation = ref<{
    estimatedSize: string
    maxSize: string
    exceedsBy: string
  } | null>(null)

  // Computed properties
  const isDocumentModified = computed(() => {
    if (!organizedDocuments.value || !props.originalDocumentState) return false

    const currentDoc = organizedDocuments.value[props.documentIndex]
    const originalDoc = props.originalDocumentState[props.documentIndex]

    if (!currentDoc || !originalDoc) return false

    // Check if name changed
    if (currentDoc.name !== originalDoc.name) return true

    // Check if pages changed
    if (currentDoc.pages.length !== originalDoc.pages.length) return true

    // Check if page order changed
    const currentPageUuids = currentDoc.pages.map((p: any) => p.uuid)
    const originalPageUuids = originalDoc.pages.map((p: any) => p.uuid)

    return JSON.stringify(currentPageUuids) !== JSON.stringify(originalPageUuids)
  })

  const documentChangeDescription = computed(() => {
    if (!isDocumentModified.value) return ''

    const currentDoc = organizedDocuments.value?.[props.documentIndex]
    const originalDoc = props.originalDocumentState?.[props.documentIndex]

    if (!currentDoc || !originalDoc) return ''

    if (currentDoc.name !== originalDoc.name) {
      return `Name changed from "${originalDoc.name}" to "${currentDoc.name}"`
    }

    if (currentDoc.pages.length !== originalDoc.pages.length) {
      const diff = currentDoc.pages.length - originalDoc.pages.length
      return diff > 0 ? `Added ${diff} pages` : `Removed ${Math.abs(diff)} pages`
    }

    return 'Page order changed'
  })

  const isDragOver = computed(() => {
    return props.dragOverDocumentGroup === props.documentIndex
  })

  const availableDocumentGroups = computed(() => {
    if (!organizedDocuments.value) return []

    return organizedDocuments.value.filter((_: any, index: number) => index !== props.documentIndex)
  })

  const canSplitWithAI = computed(() => {
    if (!organizedDocuments.value) return false

    const doc = organizedDocuments.value[props.documentIndex]
    if (!doc || !doc.pages || doc.pages.length <= 1) return false

    // Check if all pages have OCR data (simplified check)
    return doc.pages.every(
      (page: any) => page && page.images && Array.isArray(page.images) && page.images.length > 0
    )
  })

  // Methods
  const startEditing = () => {
    newName.value = props.document.name
    isEditing.value = true
  }

  const saveNewName = async () => {
    if (!newName.value.trim()) return

    const document = organizedDocuments.value[props.documentIndex]
    if (!document || !document.pages || document.pages.length === 0) {
      toast({
        title: t('error'),
        description: 'Document not found or has no pages',
        variant: 'destructive'
      })
      return
    }

    const firstPage = document.pages[0]
    const trimmedName = newName.value.trim()
    const oldName = document.name

    // Track document group renamed
    trackEvent(MetricEventType.PDF_DOCUMENT_GROUP_RENAMED, {
      old_name: oldName,
      new_name: trimmedName,
      page_count: document.pages.length,
      case_uuid: props.caseId
    })

    // Update local state immediately
    organizedDocuments.value[props.documentIndex].name = trimmedName

    // Add to pending name changes for later processing
    addPendingNameChange(firstPage.documentUuid, trimmedName)

    isEditing.value = false
    emit('documentChange')

    toast({
      title: t('success'),
      description: t('documentNameQueuedForSave'),
      variant: 'default'
    })
  }

  const cancelEditing = () => {
    isEditing.value = false
    newName.value = ''
  }

  const generateAITitle = async () => {
    if (isGeneratingTitle.value) return

    isGeneratingTitle.value = true
    try {
      const document = organizedDocuments.value[props.documentIndex]
      const firstPage = document.pages[0]

      // Prepare current organization state for the target document group
      const currentOrganizationState = {
        document_groups: [
          {
            name: document.name,
            pages: document.pages.map((page: any) => ({
              uuid: page.uuid,
              document_uuid: page.documentUuid,
              page_number: page.actualPageNumber || 1,
              filename: page.filename
            }))
          }
        ]
      }

      const response = await DocumentsService.documentsGenerateTitleCreate({
        uuid: firstPage.documentUuid,
        requestBody: {
          current_organization_state: currentOrganizationState,
          document_group_name: document.name
        }
      })

      if (response.suggested_title) {
        newName.value = response.suggested_title

        // Track AI title generated in organize view
        trackEvent(MetricEventType.PDF_DOCUMENT_TITLE_AI_GENERATED, {
          document_name: document.name,
          suggested_title: response.suggested_title,
          page_count: document.pages.length,
          case_uuid: props.caseId
        })
      }

      emit('documentChange')

      toast({
        title: t('aiTitleGenerated'),
        description: t('aiTitleGeneratedDescription'),
        variant: 'default'
      })
    } catch (error) {
      console.error('Error generating AI title:', error)
      toast({
        title: t('aiTitleError'),
        description: error instanceof Error ? error.message : t('failedToGenerateTitle'),
        variant: 'destructive'
      })
    } finally {
      isGeneratingTitle.value = false
    }
  }

  const previewDocument = async () => {
    const document = props.document
    if (document.pages && document.pages.length > 0) {
      try {
        isGeneratingPreview.value = true
        let progressToast: any = null

        // Show initial toast
        progressToast = toast({
          title: t('info'),
          description: `Generating preview... (0%)`,
          variant: 'default'
        })

        // Create PDF with progress updates
        const pdfBytes = await createPdfFromPages(
          document.pages,
          props.pageRotations,
          (progress: number) => {
            // Update toast with progress
            if (progressToast) {
              progressToast.update({
                title: t('info'),
                description: `Generating preview... (${Math.round(progress)}%)`,
                variant: 'default'
              })
            }
          }
        )

        // Clear any existing blob URLs
        if (pdfViewerUrl.value.startsWith('blob:')) {
          URL.revokeObjectURL(pdfViewerUrl.value)
        }

        // Create blob URL for the generated PDF
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const blobUrl = URL.createObjectURL(blob)

        // Set PDF data and URL for the viewer
        previewPdfData.value = pdfBytes
        pdfViewerUrl.value = blobUrl
        pdfViewerFilename.value = document.name
        previewDocumentMeta.value = {
          uuid: document.pages[0].documentUuid,
          page_count: document.pages.length,
          filesize: blob.size,
          is_pdf_modified: false,
          current_pdf_url: blobUrl,
          original_pdf_url: document.pages[0]?.url
        }
        showPdfViewer.value = true

        // Show completion toast
        // toast({
        //   title: t('success'),
        //   description: 'Preview generated successfully!',
        //   variant: 'default'
        // })
      } catch (error) {
        console.error('Error generating preview PDF:', error)
        toast({
          title: t('error'),
          description: 'Failed to generate preview. Please try again.',
          variant: 'destructive'
        })
      } finally {
        isGeneratingPreview.value = false
      }
    } else {
      toast({
        title: t('error'),
        description: t('noDocumentUrl'),
        variant: 'destructive'
      })
    }
  }

  const onPdfDocumentUpdated = (updatedDoc: any) => {
    if (!updatedDoc) return
    // Update all pages referencing this documentUuid to use the modified backend URL
    const targetUuid = previewDocumentMeta.value?.uuid
    if (!targetUuid) return
    organizedDocuments.value.forEach((doc: any) => {
      doc.pages.forEach((p: any) => {
        if (p.documentUuid === targetUuid) {
          p.url = updatedDoc.current_pdf_url || p.url
        }
      })
    })
  }

  const splitWithAI = async () => {
    if (isSplittingWithAI.value) return

    const currentDoc = organizedDocuments.value[props.documentIndex]
    if (!currentDoc || !currentDoc.pages || currentDoc.pages.length === 0) {
      toast({
        title: t('error'),
        description: 'Document not found or has no pages',
        variant: 'destructive'
      })
      return
    }

    isSplittingWithAI.value = true

    // Track AI split started
    trackEvent(MetricEventType.PDF_DOCUMENT_SPLIT_AI_STARTED, {
      document_name: currentDoc.name,
      page_count: currentDoc.pages.length,
      case_uuid: props.caseId,
      document_uuid: currentDoc.pages[0].documentUuid
    })

    try {
      toast({
        title: t('info'),
        description: 'Analyzing document with AI...',
        variant: 'default'
      })

      // Prepare organization state for ONLY the current document group
      const currentOrganizationState = {
        document_groups: [
          {
            name: currentDoc.name,
            pages: currentDoc.pages.map((page: any) => ({
              uuid: page.uuid,
              document_uuid: page.documentUuid,
              page_number: page.actualPageNumber || 1,
              filename: page.filename
            }))
          }
        ]
      }

      // Call the AI split endpoint
      const response = await DocumentsService.documentsSplitWithAiCreate({
        requestBody: {
          case_uuid: props.caseId,
          document_uuid: currentDoc.pages[0].documentUuid,
          current_organization_state: currentOrganizationState
        }
      })

      // Apply the AI split directly to frontend state
      if (response.document_groups && response.document_groups.length > 0) {
        if (response.document_groups.length === 1) {
          // Track AI split completed with no split needed
          trackEvent(MetricEventType.PDF_DOCUMENT_SPLIT_AI_COMPLETED, {
            document_name: currentDoc.name,
            groups_created: 1,
            split_occurred: false,
            case_uuid: props.caseId
          })

          toast({
            title: t('aiAnalysisComplete'),
            description: t('aiAnalysisNoSplit'),
            variant: 'default'
          })
        } else {
          // Multiple groups means a split occurred
          // Remove the current document group
          organizedDocuments.value.splice(props.documentIndex, 1)

          // Add the new AI-suggested groups
          const newGroups = response.document_groups.map((group: any) => {
            // The AI returns sequential page positions (1, 2, 3...) in the current document group
            // We need to map these back to the actual pages by their position in the array
            const matchingPages = group.pages
              .map((aiPagePosition: number) => {
                // AI page positions are 1-based, array is 0-based
                const arrayIndex = aiPagePosition - 1
                return currentDoc.pages[arrayIndex]
              })
              .filter(Boolean) // Remove any undefined pages

            console.log(
              `AI split group "${group.name}": AI pages [${group.pages.join(', ')}] -> matched ${matchingPages.length} pages`
            )

            return {
              name: group.name,
              pages: matchingPages
            }
          })

          // Insert the new groups at the same position
          organizedDocuments.value.splice(props.documentIndex, 0, ...newGroups)

          // Track AI split completed with actual split
          trackEvent(MetricEventType.PDF_DOCUMENT_SPLIT_AI_COMPLETED, {
            document_name: currentDoc.name,
            groups_created: newGroups.length,
            split_occurred: true,
            case_uuid: props.caseId,
            group_names: newGroups.map((g) => g.name),
            total_pages: currentDoc.pages.length
          })

          emit('documentChange')

          toast({
            title: t('aiSplitSuccess'),
            description: t('aiSplitDescription', { count: newGroups.length }),
            variant: 'default'
          })
        }
      }
    } catch (error) {
      console.error('Error splitting document with AI:', error)

      // Track AI split failed
      trackEvent(MetricEventType.PDF_DOCUMENT_SPLIT_AI_FAILED, {
        document_name: currentDoc.name,
        page_count: currentDoc.pages.length,
        error: error instanceof Error ? error.message : 'Unknown error',
        case_uuid: props.caseId
      })

      toast({
        title: t('error'),
        description: t('aiSplitError'),
        variant: 'destructive'
      })
    } finally {
      isSplittingWithAI.value = false
    }
  }

  const mergeWithDocumentAbove = () => {
    if (props.documentIndex === 0) {
      toast({
        title: t('error'),
        description: t('noDocumentAbove'),
        variant: 'destructive'
      })
      return
    }

    const performMerge = () => {
      const sourceDoc = organizedDocuments.value[props.documentIndex]
      const targetDoc = organizedDocuments.value[props.documentIndex - 1]

      // Track document merge
      trackEvent(MetricEventType.PDF_DOCUMENT_MERGED, {
        source_document_name: sourceDoc.name,
        target_document_name: targetDoc.name,
        source_page_count: sourceDoc.pages.length,
        target_page_count: targetDoc.pages.length,
        merge_direction: 'above',
        case_uuid: props.caseId
      })

      emit('mergeDocuments', props.documentIndex, props.documentIndex - 1)
    }

    // Check size and show warning if needed, otherwise proceed
    if (checkMergeSizeAndWarn(props.documentIndex, props.documentIndex - 1, performMerge)) {
      performMerge()
    }
  }

  const mergeWithDocumentBelow = () => {
    if (props.documentIndex === organizedDocuments.value.length - 1) {
      toast({
        title: t('error'),
        description: t('noDocumentBelow'),
        variant: 'destructive'
      })
      return
    }

    const performMerge = () => {
      const sourceDoc = organizedDocuments.value[props.documentIndex]
      const targetDoc = organizedDocuments.value[props.documentIndex + 1]

      // Track document merge
      trackEvent(MetricEventType.PDF_DOCUMENT_MERGED, {
        source_document_name: sourceDoc.name,
        target_document_name: targetDoc.name,
        source_page_count: sourceDoc.pages.length,
        target_page_count: targetDoc.pages.length,
        merge_direction: 'below',
        case_uuid: props.caseId
      })

      emit('mergeDocuments', props.documentIndex, props.documentIndex + 1)
    }

    // Check size and show warning if needed, otherwise proceed
    if (checkMergeSizeAndWarn(props.documentIndex, props.documentIndex + 1, performMerge)) {
      performMerge()
    }
  }

  const mergeWithSpecificGroup = (targetDocName: string) => {
    const targetDocIndex = organizedDocuments.value.findIndex(
      (doc: any) => doc.name === targetDocName
    )

    if (targetDocIndex === -1) {
      toast({
        title: t('error'),
        description: 'Target document not found',
        variant: 'destructive'
      })
      return
    }

    if (props.documentIndex === targetDocIndex) {
      toast({
        title: t('error'),
        description: 'Cannot merge document with itself',
        variant: 'destructive'
      })
      return
    }

    const performMerge = () => {
      const sourceDoc = organizedDocuments.value[props.documentIndex]
      const targetDoc = organizedDocuments.value[targetDocIndex]

      // Track document merge
      trackEvent(MetricEventType.PDF_DOCUMENT_MERGED, {
        source_document_name: sourceDoc.name,
        target_document_name: targetDoc.name,
        source_page_count: sourceDoc.pages.length,
        target_page_count: targetDoc.pages.length,
        merge_direction: 'specific',
        case_uuid: props.caseId
      })

      emit('mergeDocuments', props.documentIndex, targetDocIndex)
    }

    // Check size and show warning if needed, otherwise proceed
    if (checkMergeSizeAndWarn(props.documentIndex, targetDocIndex, performMerge)) {
      performMerge()
    }
  }

  const mergeAllDocuments = () => {
    if (organizedDocuments.value.length <= 1) {
      toast({
        title: t('error'),
        description: t('needMultipleDocuments'),
        variant: 'destructive'
      })
      return
    }

    const performMerge = () => {
      // Track merge all documents
      trackEvent(MetricEventType.PDF_DOCUMENTS_MERGED_ALL, {
        document_count: organizedDocuments.value.length,
        total_pages: organizedDocuments.value.reduce(
          (sum: number, doc: DocumentGroup) => sum + doc.pages.length,
          0
        ),
        case_uuid: props.caseId
      })

      // Merge all documents into the first one
      const firstDoc = organizedDocuments.value[0]
      const otherDocs = organizedDocuments.value.slice(1)

      // Collect all pages from other documents
      const allPages = [...firstDoc.pages]
      otherDocs.forEach((doc: any) => {
        allPages.push(...doc.pages)
      })

      // Update first document with all pages
      firstDoc.pages = allPages
      firstDoc.name = 'Merged Document'

      // Remove all other documents
      organizedDocuments.value.splice(1)

      emit('documentChange')

      toast({
        title: t('success'),
        description: t('documentsMerged'),
        variant: 'default'
      })
    }

    // Check size and show warning if needed, otherwise proceed
    if (checkMergeAllSizeAndWarn(performMerge)) {
      performMerge()
    }
  }

  const duplicateDocument = () => {
    const doc = organizedDocuments.value[props.documentIndex]
    const newDoc = {
      name: `${doc.name} - Copy`,
      pages: [...doc.pages]
    }

    // Track document duplicated
    trackEvent(MetricEventType.PDF_DOCUMENT_DUPLICATED, {
      document_name: doc.name,
      page_count: doc.pages.length,
      case_uuid: props.caseId
    })

    organizedDocuments.value.push(newDoc)
    emit('documentChange')

    toast({
      title: t('success'),
      description: t('documentDuplicated', { name: doc.name }),
      variant: 'default'
    })
  }

  const deleteDocument = () => {
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
  }

  const handleDelete = () => {
    const doc = organizedDocuments.value[props.documentIndex]

    // Track document group deleted
    trackEvent(MetricEventType.PDF_DOCUMENT_GROUP_DELETED, {
      document_name: doc.name,
      page_count: doc.pages.length,
      case_uuid: props.caseId
    })

    organizedDocuments.value.splice(props.documentIndex, 1)
    emit('documentChange')
    closeDeleteModal()
    toast({
      title: t('success'),
      description: t('documentDeleted'),
      variant: 'default'
    })
  }

  // Merge size warning handlers
  const closeMergeSizeWarning = () => {
    showMergeSizeWarning.value = false
    pendingMergeAction.value = null
    mergeSizeValidation.value = null
  }

  const proceedWithMerge = () => {
    if (pendingMergeAction.value) {
      pendingMergeAction.value()
    }
    closeMergeSizeWarning()
  }

  /**
   * Check merge size and show warning if it exceeds limit
   * Returns true if merge should proceed, false if warning was shown
   */
  const checkMergeSizeAndWarn = (
    sourceIndex: number,
    targetIndex: number,
    mergeAction: () => void
  ): boolean => {
    const validation = validateMergeSize(sourceIndex, targetIndex)

    if (!validation.isValid) {
      mergeSizeValidation.value = {
        estimatedSize: formatFileSize(validation.estimatedSize),
        maxSize: formatFileSize(validation.maxSize),
        exceedsBy: formatFileSize(validation.exceedsBy)
      }
      pendingMergeAction.value = mergeAction
      showMergeSizeWarning.value = true
      return false
    }

    return true
  }

  /**
   * Check merge all size and show warning if it exceeds limit
   */
  const checkMergeAllSizeAndWarn = (mergeAction: () => void): boolean => {
    const validation = validateMergeAllSize()

    if (!validation.isValid) {
      mergeSizeValidation.value = {
        estimatedSize: formatFileSize(validation.estimatedSize),
        maxSize: formatFileSize(validation.maxSize),
        exceedsBy: formatFileSize(validation.exceedsBy)
      }
      pendingMergeAction.value = mergeAction
      showMergeSizeWarning.value = true
      return false
    }

    return true
  }
</script>
